import { Controller } from "@hotwired/stimulus";

const DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 2;

export default class extends Controller {
  static targets = ["input", "list"];
  static values = { url: String };

  connect() {
    this.activeIndex = -1;
  }

  disconnect() {
    clearTimeout(this.debounceTimeout);
    this.abortController?.abort();
  }

  search() {
    clearTimeout(this.debounceTimeout);

    const query = this.inputTarget.value.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      this.close();
      return;
    }

    this.debounceTimeout = setTimeout(
      () => this.fetchSuggestions(query),
      DEBOUNCE_MS
    );
  }

  async fetchSuggestions(query) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const response = await fetch(
        `${this.urlValue}?query=${encodeURIComponent(query)}`,
        {
          headers: { Accept: "application/json" },
          signal: this.abortController.signal,
        }
      );
      if (!response.ok) return;

      this.render(await response.json());
    } catch (error) {
      if (error.name !== "AbortError") this.close();
    }
  }

  render(suggestions) {
    this.activeIndex = -1;

    if (!suggestions.length) {
      this.close();
      return;
    }

    this.listTarget.innerHTML = suggestions
      .map(
        (suggestion) => `
          <li>
            <a href="${suggestion.url}" class="search-suggest-item">${suggestion.name}</a>
          </li>
        `
      )
      .join("");

    this.open();
  }

  onKeydown(event) {
    if (this.listTarget.hidden) return;

    const items = this.listTarget.querySelectorAll(".search-suggest-item");
    if (!items.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1);
      this.highlight(items);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
      this.highlight(items);
    } else if (event.key === "Enter" && this.activeIndex >= 0) {
      event.preventDefault();
      items[this.activeIndex].click();
    } else if (event.key === "Escape") {
      this.close();
    }
  }

  highlight(items) {
    items.forEach((item, index) =>
      item.classList.toggle("active", index === this.activeIndex)
    );
  }

  onBlur() {
    // Delay so a click on a suggestion registers before the list closes.
    setTimeout(() => this.close(), 150);
  }

  open() {
    this.listTarget.hidden = false;
  }

  close() {
    this.listTarget.hidden = true;
    this.listTarget.innerHTML = "";
  }
}
